/**
 * MeetingRoomListPage — Issue #365.
 *
 * Lists administrative meeting rooms with create / archive actions gated on
 * `admin_affairs:manage`.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAffairsPermissions';
import {
  ADMIN_MEETING_ROOM_STATUS_LABELS,
  useAdminMeetingRoomMutations,
  useAdminMeetingRooms,
  type AdminMeetingRoomDto,
  type CreateAdminMeetingRoomDto,
} from '@/hooks/useAdminAffairs';

const STATUS_BADGE: Readonly<Record<AdminMeetingRoomDto['status'], string>> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-amber-100 text-amber-800',
  archived: 'bg-zinc-100 text-zinc-500',
};

export default function MeetingRoomListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_AFFAIRS_PERMISSIONS.READ);
  const canManage = hasPermission(
    permissions,
    ADMIN_AFFAIRS_PERMISSIONS.MANAGE,
  );

  const { items, total, loading, error, fetchRooms } = useAdminMeetingRooms();
  const {
    loading: actionLoading,
    error: actionError,
    createRoom,
    archiveRoom,
  } = useAdminMeetingRoomMutations();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateAdminMeetingRoomDto>({
    roomCode: '',
    name: '',
  });

  useEffect(() => {
    if (!canRead) return;
    void fetchRooms({ includeArchived: false });
  }, [canRead, fetchRooms]);

  if (!canRead) return <AccessDenied resource="会议室" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">会议室主数据</h1>
          <p className="text-sm text-slate-500">
            维护会议室名称 / 容量 / 设备，停用 / 归档不再使用的会议室。
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="meeting-room-create-entry"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? '取消' : '新增会议室 →'}
          </button>
        ) : null}
      </header>

      {creating && canManage ? (
        <Card className="space-y-3 p-4">
          <label className="block text-sm">
            会议室编码
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.roomCode}
              onChange={(e) =>
                setForm((f) => ({ ...f, roomCode: e.target.value }))
              }
              data-testid="meeting-room-create-code"
            />
          </label>
          <label className="block text-sm">
            会议室名称
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.name}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
              data-testid="meeting-room-create-name"
            />
          </label>
          <label className="block text-sm">
            容量
            <input
              type="number"
              min={1}
              className="mt-1 block w-32 rounded border border-slate-300 px-2 py-1"
              value={form.capacity ?? ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  capacity: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              data-testid="meeting-room-create-capacity"
            />
          </label>
          <label className="block text-sm">
            位置
            <input
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1"
              value={form.location ?? ''}
              onChange={(e) =>
                setForm((f) => ({ ...f, location: e.target.value }))
              }
            />
          </label>
          <button
            type="button"
            disabled={actionLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
            data-testid="meeting-room-create-submit"
            onClick={async () => {
              const created = await createRoom(form);
              if (created) {
                setForm({ roomCode: '', name: '' });
                setCreating(false);
                await fetchRooms({ includeArchived: false });
              }
            }}
          >
            {actionLoading ? '提交中…' : '提交'}
          </button>
          {actionError ? (
            <p className="text-sm text-red-600" data-testid="meeting-room-create-error">
              {actionError.message}
            </p>
          ) : null}
        </Card>
      ) : null}

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">编码</th>
              <th className="px-3 py-2">名称</th>
              <th className="px-3 py-2">容量</th>
              <th className="px-3 py-2">位置</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={6} data-testid="meeting-room-loading">
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={6} data-testid="meeting-room-empty">
                  暂无会议室
                </td>
              </tr>
            ) : (
              items.map((room) => (
                <tr key={room.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{room.roomCode}</td>
                  <td className="px-3 py-2">{room.name}</td>
                  <td className="px-3 py-2">{room.capacity ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{room.location ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE[room.status]}`}
                    >
                      {ADMIN_MEETING_ROOM_STATUS_LABELS[room.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {canManage && room.status !== 'archived' ? (
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        data-testid={`meeting-room-archive-${room.id}`}
                        onClick={async () => {
                          await archiveRoom(room.id, room.version);
                          await fetchRooms({ includeArchived: false });
                        }}
                      >
                        归档
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-500">合计 {total} 间会议室</p>
    </div>
  );
}
